import Script from 'next/script';
import type { JSX } from 'react';
import type { AnalyticsIntegrationsConfig } from '../portal';

/**
 * Renders {@code <Script>} tags for each provider the tenant has
 * configured in {@code ShellConfig.integrations}. Server component
 * — no client state, just markup; mount once per tenant layout.
 *
 * <h3>Design constraints</h3>
 * <ol>
 *   <li><b>Tenant input is an ID, never a URL.</b> Admins type
 *       {@code GTM-ABC123}, not {@code <script src="...">}. We
 *       construct the vetted loader URL ourselves. A "paste any
 *       HTML" field would be an XSS hole that lets anyone with admin
 *       access exfiltrate user sessions.</li>
 *   <li><b>Validate every ID before rendering.</b> One regex per
 *       provider; a malformed ID renders nothing and logs a warning
 *       to the server console (the bad value never leaks to the
 *       client console because it might contain a credential typo'd
 *       into the wrong field).</li>
 *   <li><b>Loading strategy per provider.</b> GTM and GA4 are
 *       {@code afterInteractive} so events fire early without
 *       blocking first paint. Hotjar is {@code lazyOnload} because
 *       session replay isn't critical-path.</li>
 *   <li><b>Consent gating.</b> When
 *       {@code integrations.deferUntilConsent} is true, every loader
 *       runs inside an inline poll that waits for
 *       {@code window.__lmsConsent === true} before injecting the
 *       provider's script. Hosts flip the flag via their own CMP
 *       integration. This package does NOT ship a CMP.</li>
 *   <li><b>No double-loading enforcement.</b> Running {@code gtmId}
 *       and {@code ga4MeasurementId} together causes double-counted
 *       events. Admin schemas should warn; this loader allows the
 *       combo because hosts might be migrating gradually.</li>
 * </ol>
 *
 * <h3>Not in scope</h3>
 * <ul>
 *   <li>A consent management platform. Hosts own that.</li>
 *   <li>A consent UI. No banner, no preference center.</li>
 *   <li>Pixel-perfect GTM export. Hosts that need server-side
 *       tagging, custom container snippets, or the Conversion API
 *       should embed those directly.</li>
 * </ul>
 */
interface Props {
  integrations: AnalyticsIntegrationsConfig | undefined;
}

const VALIDATORS: Record<string, RegExp> = {
  gtmId: /^GTM-[A-Z0-9]{4,12}$/,
  ga4MeasurementId: /^G-[A-Z0-9]{6,12}$/,
  metaPixelId: /^[0-9]{6,20}$/,
  hotjarSiteId: /^[0-9]{4,10}$/,
  hubspotHubId: /^[0-9]{4,12}$/,
  // Domain — lowercase letters, digits, hyphens, dots; at least one dot.
  // Not a strict TLD validator (those age badly).
  plausibleDomain: /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/,
};

function isValid(field: keyof typeof VALIDATORS, value: string | undefined): boolean {
  if (!value || typeof value !== 'string') return false;
  const re = VALIDATORS[field];
  if (!re) return false;
  const ok = re.test(value);
  if (!ok && process.env['NODE_ENV'] !== 'production') {
    console.warn(
      `[AnalyticsScripts] Invalid ${field} value (length ${value.length}); skipping. Check the format in ShellConfig.integrations.`,
    );
  }
  return ok;
}

function consentGate(loaderJs: string): string {
  return `(function(){
    var tries = 0;
    function tick() {
      if (window.__lmsConsent === true) { ${loaderJs} return; }
      if (++tries > 60) return;
      setTimeout(tick, 500);
    }
    tick();
  })();`;
}

export function AnalyticsScripts({ integrations }: Props) {
  if (!integrations) return null;

  const gate = integrations.deferUntilConsent === true;
  const out: JSX.Element[] = [];

  if (isValid('gtmId', integrations.gtmId)) {
    const gtmId = integrations.gtmId!;
    const loader = `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer',${JSON.stringify(gtmId)});`;
    out.push(
      <Script
        id="lms-gtm"
        key="gtm"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: gate ? consentGate(loader) : loader }}
      />,
    );
  }

  if (isValid('ga4MeasurementId', integrations.ga4MeasurementId)) {
    const id = integrations.ga4MeasurementId!;
    const loader = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', ${JSON.stringify(id)});`;
    out.push(
      <Script
        key="ga4-loader"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`}
      />,
      <Script
        id="lms-ga4"
        key="ga4-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: gate ? consentGate(loader) : loader }}
      />,
    );
  }

  if (isValid('metaPixelId', integrations.metaPixelId)) {
    const id = integrations.metaPixelId!;
    const loader = `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', ${JSON.stringify(id)});
fbq('track', 'PageView');`;
    out.push(
      <Script
        id="lms-meta-pixel"
        key="meta-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: gate ? consentGate(loader) : loader }}
      />,
    );
  }

  if (isValid('hotjarSiteId', integrations.hotjarSiteId)) {
    const id = integrations.hotjarSiteId!;
    const loader = `
(function(h,o,t,j,a,r){
    h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
    h._hjSettings={hjid:${JSON.stringify(parseInt(id, 10))},hjsv:6};
    a=o.getElementsByTagName('head')[0];
    r=o.createElement('script');r.async=1;
    r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
    a.appendChild(r);
})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;
    out.push(
      <Script
        id="lms-hotjar"
        key="hotjar"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{ __html: gate ? consentGate(loader) : loader }}
      />,
    );
  }

  if (isValid('hubspotHubId', integrations.hubspotHubId)) {
    const id = integrations.hubspotHubId!;
    const src = `https://js.hs-scripts.com/${encodeURIComponent(id)}.js`;
    if (gate) {
      const loader = `
var s=document.createElement('script');
s.async=true; s.defer=true; s.id='hs-script-loader';
s.src=${JSON.stringify(src)};
document.head.appendChild(s);`;
      out.push(
        <Script
          id="lms-hubspot"
          key="hubspot"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{ __html: consentGate(loader) }}
        />,
      );
    } else {
      out.push(
        <Script
          id="hs-script-loader"
          key="hubspot"
          strategy="afterInteractive"
          src={src}
          async
          defer
        />,
      );
    }
  }

  if (isValid('plausibleDomain', integrations.plausibleDomain)) {
    const domain = integrations.plausibleDomain!;
    if (gate) {
      // `s.async=false` is load-bearing: a script created via
      // createElement() is force-async, and Plausible's tracker reads
      // `data-domain` / `data-api` off `document.currentScript`, which is
      // null for async / out-of-order scripts. Clearing async makes it an
      // in-order script so currentScript resolves.
      const loader = `
var s=document.createElement('script');
s.async=false; s.defer=true; s.setAttribute('data-domain', ${JSON.stringify(domain)});
s.src='https://plausible.io/js/script.js';
document.head.appendChild(s);`;
      out.push(
        <Script
          id="lms-plausible"
          key="plausible"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{ __html: consentGate(loader) }}
        />,
      );
    } else {
      // Plain parser-inserted <script> — NOT next/script. Same reason as
      // Welcoming Web below: Plausible's tracker reads `data-domain` off
      // `document.currentScript`, which next/script's dynamic injection
      // leaves null. `defer` keeps it off the critical path while still
      // being an in-order script (currentScript works for defer).
      out.push(
        <script
          key="plausible"
          src="https://plausible.io/js/script.js"
          data-domain={domain}
          defer
        />,
      );
    }
  }

  if (integrations.welcomingWebId) {
    // Deliberately a plain, parser-inserted <script> — NOT next/script.
    // The a11y-widget bundle reads its widget id off `document.currentScript`,
    // which is `null` for any script injected dynamically (exactly what
    // next/script's afterInteractive / lazyOnload strategies do). Rendered
    // from this server component the tag lands in the SSR HTML verbatim —
    // the same shape the vendor documents:
    //   <script src="…/a11y-widget.bundle.js" data-widget-id="…"></script>
    out.push(
      <script
        key="welcoming-web"
        src="https://cdn-01.welcomingweb.com/a11y-widget.bundle.js"
        data-widget-id={integrations.welcomingWebId}
      />,
    );
  }

  if (out.length === 0) return null;
  return <>{out}</>;
}
